export const SNAKE_STATE_VERSION = 1;

const PLAYER_SNAKE_TYPE = 'player-snake';
const TURN_RATE = Math.PI * 1.5;
const BASE_SPEED = 1.25;
const SPRINT_MULTIPLIER = 1.8;
const REVERSE_MULTIPLIER = 0.5;
const MAX_VITAL = 100;
const MIN_TEMPERATURE = -20;
const MAX_TEMPERATURE = 60;
const SEGMENT_SPACING = 0.25;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteRange(value, min, max, name) {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${name} must be between ${min} and ${max}`);
  }
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeAngle(value) {
  const angle = assertFinite(value, 'heading') % (Math.PI * 2);
  return angle < 0 ? angle + (Math.PI * 2) : angle;
}

function normalizePosition(position = {}, fallback = { x: 0, y: 0, z: 0 }) {
  assertPlainObject(position, 'position');
  return {
    x: assertFinite(position.x ?? fallback.x, 'position.x'),
    y: assertFinite(position.y ?? fallback.y, 'position.y'),
    z: assertFinite(position.z ?? fallback.z, 'position.z'),
  };
}

function normalizeVitals(vitals = {}, fallback = {}) {
  assertPlainObject(vitals, 'vitals');
  return {
    health: assertFiniteRange(vitals.health ?? fallback.health ?? MAX_VITAL, 0, MAX_VITAL, 'vitals.health'),
    hunger: assertFiniteRange(vitals.hunger ?? fallback.hunger ?? 0, 0, MAX_VITAL, 'vitals.hunger'),
    stamina: assertFiniteRange(vitals.stamina ?? fallback.stamina ?? MAX_VITAL, 0, MAX_VITAL, 'vitals.stamina'),
    temperature: assertFiniteRange(
      vitals.temperature ?? fallback.temperature ?? 25,
      MIN_TEMPERATURE,
      MAX_TEMPERATURE,
      'vitals.temperature',
    ),
  };
}

function deriveGrowthStage(length) {
  if (length < 1) return 'hatchling';
  if (length < 2.5) return 'juvenile';
  return 'adult';
}

function normalizeGrowth(growth = {}, fallback = {}, length) {
  assertPlainObject(growth, 'growth');
  const points = assertFiniteNonNegative(growth.points ?? fallback.points ?? 0, 'growth.points');
  const stage = growth.stage === undefined
    ? deriveGrowthStage(length)
    : assertNonEmptyString(growth.stage, 'growth.stage');
  if (stage !== deriveGrowthStage(length)) {
    throw new RangeError('growth.stage must match snake length');
  }
  return { stage, points };
}

function buildBodySegments(position, heading, length) {
  const segmentCount = Math.max(2, Math.ceil(length / SEGMENT_SPACING) + 1);
  const spacing = length / (segmentCount - 1);
  const directionX = Math.sin(heading);
  const directionZ = Math.cos(heading);
  return Array.from({ length: segmentCount }, (_, index) => ({
    index,
    x: position.x - (directionX * spacing * index),
    y: position.y,
    z: position.z - (directionZ * spacing * index),
  }));
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeSnakeStateChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function buildState({
  entityId,
  revision,
  species,
  position,
  heading,
  speed,
  length,
  mass,
  vitals,
  growth,
  tick,
  simulationTime,
  stepSeconds,
}) {
  const normalizedEntityId = assertNonEmptyString(entityId, 'entityId');
  const normalizedRevision = assertPositiveInteger(revision, 'revision');
  const normalizedSpecies = assertNonEmptyString(species, 'species');
  const normalizedPosition = normalizePosition(position);
  const normalizedHeading = normalizeAngle(heading);
  const normalizedSpeed = assertFinite(speed, 'speed');
  const normalizedLength = assertPositiveFinite(length, 'length');
  const normalizedMass = assertPositiveFinite(mass, 'mass');
  const normalizedVitals = normalizeVitals(vitals);
  const normalizedGrowth = normalizeGrowth(growth, {}, normalizedLength);
  const normalizedTick = tick === null ? null : assertNonNegativeInteger(tick, 'tick');
  const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
  const normalizedStepSeconds = stepSeconds === null
    ? null
    : assertPositiveFinite(stepSeconds, 'stepSeconds');
  const lifecycle = normalizedVitals.health > 0 ? 'alive' : 'dead';
  const payload = {
    version: SNAKE_STATE_VERSION,
    entityId: normalizedEntityId,
    revision: normalizedRevision,
    species: normalizedSpecies,
    lifecycle,
    position: normalizedPosition,
    heading: normalizedHeading,
    speed: lifecycle === 'alive' ? normalizedSpeed : 0,
    length: normalizedLength,
    mass: normalizedMass,
    bodySegments: buildBodySegments(normalizedPosition, normalizedHeading, normalizedLength),
    vitals: normalizedVitals,
    growth: normalizedGrowth,
    tick: normalizedTick,
    simulationTime: normalizedSimulationTime,
    stepSeconds: normalizedStepSeconds,
  };
  return deepFreeze({
    ...payload,
    checksum: computeSnakeStateChecksum(payload),
  });
}

function normalizeSpawnOptions(options = {}) {
  assertPlainObject(options, 'options');
  if (options.revision !== undefined || options.lifecycle !== undefined || options.checksum !== undefined) {
    throw new RangeError('snake revision, lifecycle, and checksum are authoritative');
  }
  const length = assertPositiveFinite(options.length ?? 0.6, 'length');
  return {
    entityId: options.entityId === undefined
      ? undefined
      : assertNonEmptyString(options.entityId, 'entityId'),
    species: assertNonEmptyString(options.species ?? 'common-water-snake', 'species'),
    position: normalizePosition(options.position ?? {}),
    heading: normalizeAngle(options.heading ?? 0),
    speed: 0,
    length,
    mass: assertPositiveFinite(options.mass ?? 0.12, 'mass'),
    vitals: normalizeVitals(options.vitals ?? {}),
    growth: normalizeGrowth(options.growth ?? {}, {}, length),
    tick: null,
    simulationTime: 0,
    stepSeconds: null,
  };
}

function entityComponents(stateLike, revision) {
  const lifecycle = stateLike.vitals.health > 0 ? 'alive' : 'dead';
  return {
    role: 'player',
    species: stateLike.species,
    lifecycle,
    heading: stateLike.heading,
    speed: lifecycle === 'alive' ? stateLike.speed : 0,
    length: stateLike.length,
    mass: stateLike.mass,
    bodySegments: buildBodySegments(stateLike.position, stateLike.heading, stateLike.length),
    vitals: clone(stateLike.vitals),
    growth: clone(stateLike.growth),
    snakeRevision: revision,
  };
}

function entityTags(lifecycle) {
  return ['player', 'snake', lifecycle].sort();
}

function normalizeInput(input) {
  if (!input || typeof input !== 'object') throw new TypeError('input must be an object');
  const move = input.move ?? {};
  if (!move || typeof move !== 'object') throw new TypeError('input.move must be an object');
  return {
    moveX: clamp(assertFinite(move.x ?? 0, 'input.move.x'), -1, 1),
    moveY: clamp(assertFinite(move.y ?? 0, 'input.move.y'), -1, 1),
    sprint: Boolean(input.actions?.sprint?.down),
  };
}

function traversalProfile(state) {
  const radius = clamp(0.08 + (state.mass * 0.2), 0.08, 0.35);
  return {
    radius,
    height: radius * 2,
    capabilities: {
      swim: true,
      climb: true,
      crawl: true,
    },
  };
}

export class SnakeState {
  constructor({ entityWorld, traversalWorld = null }) {
    assertMethod(entityWorld, 'spawn', 'entityWorld');
    assertMethod(entityWorld, 'update', 'entityWorld');
    assertMethod(entityWorld, 'remove', 'entityWorld');
    assertMethod(entityWorld, 'get', 'entityWorld');
    assertMethod(entityWorld, 'snapshot', 'entityWorld');
    assertMethod(entityWorld, 'restore', 'entityWorld');
    if (traversalWorld !== null) {
      assertMethod(traversalWorld, 'resolveMovement', 'traversalWorld');
      assertMethod(traversalWorld, 'commitActorResolution', 'traversalWorld');
      assertMethod(traversalWorld, 'syncActor', 'traversalWorld');
      assertMethod(traversalWorld, 'removeActor', 'traversalWorld');
      assertMethod(traversalWorld, 'snapshot', 'traversalWorld');
      assertMethod(traversalWorld, 'restore', 'traversalWorld');
    }
    this.entityWorld = entityWorld;
    this.traversalWorld = traversalWorld;
    this.state = null;
  }

  ownsEntity(id) {
    return this.state !== null && this.state.entityId === id;
  }

  #syncTraversal(state, resolution = undefined) {
    if (!this.traversalWorld) return null;
    const profile = traversalProfile(state);
    if (resolution) {
      return this.traversalWorld.commitActorResolution(resolution, {
        tick: state.tick,
        simulationTime: state.simulationTime,
        stepSeconds: state.stepSeconds,
      });
    }
    return this.traversalWorld.syncActor({
      actorId: state.entityId,
      actorKind: 'snake',
      position: state.position,
      ...profile,
      tick: state.tick,
      simulationTime: state.simulationTime,
      stepSeconds: state.stepSeconds,
    });
  }

  spawn(options = {}) {
    if (this.state) throw new RangeError('A player snake already exists');
    const normalized = normalizeSpawnOptions(options);
    const lifecycle = normalized.vitals.health > 0 ? 'alive' : 'dead';
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      const entity = this.entityWorld.spawn({
        ...(normalized.entityId ? { id: normalized.entityId } : {}),
        type: PLAYER_SNAKE_TYPE,
        position: normalized.position,
        tags: entityTags(lifecycle),
        components: entityComponents(normalized, 1),
      });
      const state = buildState({
        ...normalized,
        entityId: entity.id,
        revision: 1,
      });
      this.#syncTraversal(state);
      this.state = state;
      return state;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      this.state = null;
      throw error;
    }
  }

  #commit(values, traversalResolution = undefined) {
    if (!this.state) throw new Error('No player snake exists');
    const current = this.state;
    const next = buildState({
      ...values,
      entityId: current.entityId,
      revision: current.revision + 1,
    });
    const entity = this.entityWorld.get(current.entityId);
    if (!entity) throw new Error(`Snake entity is missing: ${current.entityId}`);
    if (entity.type !== PLAYER_SNAKE_TYPE) {
      throw new RangeError(`Snake entity has invalid type: ${entity.type}`);
    }
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      this.entityWorld.update(current.entityId, {
        position: next.position,
        tags: entityTags(next.lifecycle),
        components: entityComponents(next, next.revision),
      });
      this.#syncTraversal(next, traversalResolution);
      this.state = next;
      return next;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      this.state = current;
      throw error;
    }
  }

  step({ stepSeconds, tick, simulationTime, input }) {
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedInput = normalizeInput(input);
    if (!this.state) return null;

    const current = this.state;
    const heading = normalizeAngle(
      current.heading + (normalizedInput.moveX * TURN_RATE * normalizedStepSeconds),
    );
    const canSprint = current.lifecycle === 'alive'
      && normalizedInput.moveY > 0
      && normalizedInput.sprint
      && current.vitals.stamina > 0;
    const speedMultiplier = normalizedInput.moveY < 0
      ? REVERSE_MULTIPLIER
      : canSprint ? SPRINT_MULTIPLIER : 1;
    const requestedSpeed = current.lifecycle === 'alive'
      ? normalizedInput.moveY * BASE_SPEED * speedMultiplier
      : 0;
    const requestedPosition = {
      x: current.position.x + (Math.sin(heading) * requestedSpeed * normalizedStepSeconds),
      y: current.position.y,
      z: current.position.z + (Math.cos(heading) * requestedSpeed * normalizedStepSeconds),
    };
    const traversalResolution = this.traversalWorld
      ? this.traversalWorld.resolveMovement({
        actorId: current.entityId,
        actorKind: 'snake',
        from: current.position,
        to: requestedPosition,
        ...traversalProfile(current),
      })
      : null;
    const position = traversalResolution?.position ?? requestedPosition;
    const resolvedHorizontalDistance = Math.hypot(
      position.x - current.position.x,
      position.z - current.position.z,
    );
    const speed = requestedSpeed === 0
      ? 0
      : Math.sign(requestedSpeed) * (resolvedHorizontalDistance / normalizedStepSeconds);
    const stamina = clamp(
      current.vitals.stamina + ((canSprint ? -25 : 12) * normalizedStepSeconds),
      0,
      MAX_VITAL,
    );
    const hunger = clamp(
      current.vitals.hunger
        + ((0.35 + (Math.abs(speed) * 0.08)) * normalizedStepSeconds),
      0,
      MAX_VITAL,
    );
    const health = clamp(
      current.vitals.health - (hunger >= MAX_VITAL ? 3 * normalizedStepSeconds : 0),
      0,
      MAX_VITAL,
    );

    return this.#commit({
      ...current,
      position,
      heading,
      speed: health > 0 ? speed : 0,
      vitals: {
        ...current.vitals,
        health,
        hunger,
        stamina,
      },
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    }, traversalResolution ?? undefined);
  }

  damage(amount) {
    const normalizedAmount = assertPositiveFinite(amount, 'amount');
    if (!this.state) throw new Error('No player snake exists');
    return this.#commit({
      ...this.state,
      speed: 0,
      vitals: {
        ...this.state.vitals,
        health: clamp(this.state.vitals.health - normalizedAmount, 0, MAX_VITAL),
      },
    });
  }

  feed(amount) {
    const normalizedAmount = assertPositiveFinite(amount, 'amount');
    if (!this.state) throw new Error('No player snake exists');
    return this.#commit({
      ...this.state,
      vitals: {
        ...this.state.vitals,
        hunger: clamp(this.state.vitals.hunger - normalizedAmount, 0, MAX_VITAL),
      },
    });
  }

  grow({ lengthDelta = 0.1, massDelta = 0.02, pointsDelta = 1 } = {}) {
    const normalizedLengthDelta = assertFiniteNonNegative(lengthDelta, 'lengthDelta');
    const normalizedMassDelta = assertFiniteNonNegative(massDelta, 'massDelta');
    const normalizedPointsDelta = assertFiniteNonNegative(pointsDelta, 'pointsDelta');
    if (normalizedLengthDelta === 0 && normalizedMassDelta === 0 && normalizedPointsDelta === 0) {
      throw new RangeError('At least one growth delta must be greater than zero');
    }
    if (!this.state) throw new Error('No player snake exists');
    const length = this.state.length + normalizedLengthDelta;
    return this.#commit({
      ...this.state,
      length,
      mass: this.state.mass + normalizedMassDelta,
      growth: {
        stage: deriveGrowthStage(length),
        points: this.state.growth.points + normalizedPointsDelta,
      },
    });
  }

  teleport({ position, heading = undefined }) {
    if (!this.state) throw new Error('No player snake exists');
    const normalizedPosition = normalizePosition(position, this.state.position);
    const normalizedHeading = heading === undefined
      ? this.state.heading
      : normalizeAngle(heading);
    return this.#commit({
      ...this.state,
      position: normalizedPosition,
      heading: normalizedHeading,
      speed: 0,
    });
  }

  snapshot() {
    return this.state;
  }

  exportSnapshot() {
    return this.state ? JSON.stringify(this.state) : null;
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== SNAKE_STATE_VERSION) {
      throw new RangeError(`Unsupported snake state version: ${parsed.version}`);
    }
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== computeSnakeStateChecksum(parsed)) {
      throw new RangeError('snapshot checksum is invalid');
    }
    const restored = buildState({
      entityId: parsed.entityId,
      revision: parsed.revision,
      species: parsed.species,
      position: parsed.position,
      heading: parsed.heading,
      speed: parsed.speed,
      length: parsed.length,
      mass: parsed.mass,
      vitals: parsed.vitals,
      growth: parsed.growth,
      tick: parsed.tick,
      simulationTime: parsed.simulationTime,
      stepSeconds: parsed.stepSeconds,
    });
    if (restored.checksum !== parsed.checksum) {
      throw new RangeError('snapshot state is not canonical');
    }
    if (this.state && this.state.entityId !== restored.entityId) {
      throw new RangeError('Cannot replace the active snake with a different entity ID');
    }

    const existing = this.entityWorld.get(restored.entityId);
    if (existing && existing.type !== PLAYER_SNAKE_TYPE) {
      throw new RangeError(`Snapshot entity has invalid type: ${existing.type}`);
    }
    const previous = this.state;
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      if (existing) {
        this.entityWorld.update(restored.entityId, {
          position: restored.position,
          tags: entityTags(restored.lifecycle),
          components: entityComponents(restored, restored.revision),
        });
      } else {
        this.entityWorld.spawn({
          id: restored.entityId,
          type: PLAYER_SNAKE_TYPE,
          position: restored.position,
          tags: entityTags(restored.lifecycle),
          components: entityComponents(restored, restored.revision),
        });
      }
      this.#syncTraversal(restored);
      this.state = restored;
      return restored;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      this.state = previous;
      throw error;
    }
  }

  reset({ removeEntity = true } = {}) {
    if (typeof removeEntity !== 'boolean') throw new TypeError('removeEntity must be a boolean');
    if (this.state && removeEntity && this.entityWorld.get(this.state.entityId)) {
      this.entityWorld.remove(this.state.entityId);
    }
    if (this.state && this.traversalWorld) this.traversalWorld.removeActor(this.state.entityId);
    this.state = null;
    return this.getStatus();
  }

  getStateRef() {
    if (!this.state) return null;
    return deepFreeze({
      version: SNAKE_STATE_VERSION,
      entityId: this.state.entityId,
      revision: this.state.revision,
      lifecycle: this.state.lifecycle,
      tick: this.state.tick,
      checksum: this.state.checksum,
    });
  }

  getStatus() {
    if (!this.state) {
      return deepFreeze({
        version: SNAKE_STATE_VERSION,
        active: false,
        entityId: null,
        revision: 0,
        lifecycle: null,
        tick: null,
        checksum: null,
      });
    }
    return deepFreeze({
      version: SNAKE_STATE_VERSION,
      active: true,
      entityId: this.state.entityId,
      revision: this.state.revision,
      lifecycle: this.state.lifecycle,
      tick: this.state.tick,
      checksum: this.state.checksum,
    });
  }
}
